import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import { makeStyles, Theme } from '@material-ui/core/styles'
import FilterListIcon from '@material-ui/icons/FilterList'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import ListIcon from '@material-ui/icons/List'
import PrintIcon from '@material-ui/icons/Print'
import SearchIcon from '@material-ui/icons/Search'
import ViewModuleIcon from '@material-ui/icons/ViewModule'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { sharedTableStyles } from '.'
import { TableSettings, TableSettingsProps } from './settings/TableSettings'

const tableTitleSettingsStyles = makeStyles((theme: Theme) => ({
    expandCollapseAllContainer: {
        '& button': {
            borderRadius: theme.spacing(2.5),
            marginRight: theme.spacing(0.5),
        },
        '& button:first-child': {
            marginRight: theme.spacing(1),
        },
    },
    tableTitleSettingsContainer: {
        minHeight: 50,
        '&.dark': {
            backgroundColor: '#343334',
            color: '#fff',
            '& button': {
                ...theme.typography.body2,
                color: 'inherit',
            },
            '& .MuiDivider-vertical': {
                backgroundColor: '#fff',
            },
        },
        '&.light': {
            color: '#4F4F4F',
            '& button': {
                ...theme.typography.body2,
                color: 'inherit',
            },
            '& .MuiDivider-vertical': {
                backgroundColor: '#4F4F4F',
            },
            '& .tableSearchInput': {
                border: '1px solid #4F4F4F',
            },
            '& svg': {
                borderColor: '#4F4F4F',
            },
        },
    },
    tableSettingsPadding: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1),
    },
    tableSettingsFlexCenter: {
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    viewAsContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    iconBorder: {
        border: '1px solid',
        borderRadius: 2,
    },
    activeIconBackgroundColor: {
        backgroundColor: '#13B5EA',
        color: '#fff',
    },
    displayFlex: {
        display: 'flex',
    },
    displayNone: {
        display: 'none',
    },
    searchInput: {
        fontSize: pxToRem(14),
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1),
        '& input': {
            marginLeft: theme.spacing(1),
        },
        '&.MuiInput-underline:after,&.MuiInput-underline:before,&.MuiInput-underline:hover:not(.Mui-disabled):before': {
            transition: 'unset',
            borderBottom: 'unset',
            transform: 'unset',
        },
        '& .MuiInputAdornment-positionEnd': {
            marginRight: theme.spacing(1),
        },
    },
    verticalDivider: {
        height: '70%',
        marginTop: theme.spacing(1),
    },
}))

export interface SpecTableTitleSettingsProps<TData extends Object> {
    /**
     * This hides the collapse all / expand all entirely if there is no grouping. Default false
     */
    hasGrouping?: boolean
    /**
     * The initial global search value.
     */
    initialGlobalSearchValue?: string
    /**
     * The event that gets called when a user toggles to card view.  This will hide the whole table.
     * If onCardViewClick and onTableViewClick is undefined,
     * it will hide the whole View As option on the table title settings row.
     */
    onCardViewClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when collapse all gets clicked.  If undefined, it will hide the
     * Expand All / Collapse All buttons. It won't show if hasGrouping is false.
     */
    onCollapseAllClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when expand all gets clicked.  If undefined, it will hide the
     * Expand All / Collapse All buttons. It won't show if hasGrouping is false.
     */
    onExpandAllClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when the filter button gets clicked.  If undefined, filter button will not show.
     */
    onFilterClick?: (showFilters: boolean) => void
    /**
     * The event that gets called when Clear all filter clicked.
     * If undefined, it will hide the Clear All filter button.
     */
    onClearAllFilterClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The global search value changes.  If this is undefined, global search will be hidden from the UI.
     * This is debounced by 300ms similar to filters.
     */
    onGlobalSearchValueChange?: (searchValue: string) => void
    /**
     * On print event handler.  Hides the printer when onPrintClick is undefined.
     */
    onPrintClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when a user toggles to table view.
     * If onCardViewClick and onTableViewClick is undefined,
     * it will hide the whole View As option on the table title settings row.
     */
    onTableViewClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * Show or hide the filters row.  Not to confuse with showFilterOption which show or hides the option to toggle this option.
     * This is the default value of the filter option component. Default: true
     */
    showFiltersRow?: boolean
    /**
     * Show or hide the Clear All button.
     * Default: false
     */
    showClearAllFilterButton?: boolean
    /**
     * Show or hide the table settings icon
     * If true and the table settings is undefined, it won't show the settings icon
     * Default: true
     */
    showTableSettings?: boolean
    /**
     * Settings configuration. If undefined, hides the cog/settings button.
     */
    tableSettings?: TableSettingsProps<TData>
    /**
     * Table title
     */
    tableTitle?: JSX.Element | string
    /**
     * Sets the table view icon to active. Default true
     */
    tableViewActive?: boolean
    /**
     * Default is dark
     */
    theme?: 'dark' | 'light'
    /**
     * The width of the bar.  If not passed, it will use the width of the table if there is any
     */
    width?: string
}

const SpecTableTitleSettings = <TData extends Object>({
    hasGrouping = false,
    initialGlobalSearchValue,
    onCardViewClick,
    onCollapseAllClick,
    onExpandAllClick,
    onFilterClick,
    onClearAllFilterClick,
    onGlobalSearchValueChange,
    onPrintClick,
    onTableViewClick,
    showFiltersRow = true,
    showClearAllFilterButton = false,
    showTableSettings = true,
    tableTitle,
    theme = 'dark',
    tableSettings,
    tableViewActive = true,
    width,
}: SpecTableTitleSettingsProps<TData>) => {
    const classes = tableTitleSettingsStyles()
    const { cellPadding } = sharedTableStyles()
    const searchInputRef = useRef<HTMLInputElement>()

    const [searchFocusedIn, setSearchFocusedIn] = useState<boolean>(false)

    const debouncedSearchValueChange = useCallback(
        debounce(onGlobalSearchValueChange!),
        []
    )

    const handleFilterClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        onFilterClick?.(!showFiltersRow)
    }

    const handleGlobalSearchValueChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        if (onGlobalSearchValueChange)
            debouncedSearchValueChange(event.currentTarget.value)
    }

    const handleSearchBlur = (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (!event.currentTarget.value) setSearchFocusedIn(false)
    }

    const handleSearchButtonClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setSearchFocusedIn(true)
    }

    useEffect(() => {
        if (searchFocusedIn) searchInputRef.current?.focus()
    }, [searchFocusedIn])

    return (
        <GridContainer
            alignItems="center"
            className={clsx(
                'table-title-settings-container',
                classes.tableTitleSettingsContainer,
                cellPadding,
                theme
            )}
            style={{ width }}
        >
            <GridItem xs={4} container justify="flex-start">
                <GridItem className="table-title-container">
                    {tableTitle}
                </GridItem>
            </GridItem>
            <GridItem
                xs={8}
                container
                justify="flex-end"
                className="table-options"
            >
                {!!onGlobalSearchValueChange && (
                    <GridItem
                        className={clsx(
                            classes.tableSettingsPadding,
                            classes.tableSettingsFlexCenter,
                            'global-search-container'
                        )}
                    >
                        <Button
                            onClick={handleSearchButtonClick}
                            className={clsx(
                                'global-search-button',
                                searchFocusedIn
                                    ? classes.displayNone
                                    : classes.displayFlex
                            )}
                        >
                            Search &nbsp;
                            <SearchIcon fontSize="small" />
                        </Button>
                        <Input
                            style={{ backgroundColor: 'white' }}
                            placeholder="Search"
                            inputRef={searchInputRef}
                            onChange={handleGlobalSearchValueChange}
                            onBlur={handleSearchBlur}
                            defaultValue={initialGlobalSearchValue}
                            className={clsx(
                                'tableSearchInput',
                                classes.searchInput,
                                searchFocusedIn
                                    ? classes.displayFlex
                                    : classes.displayNone
                            )}
                            endAdornment={
                                <InputAdornment position="end">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            }
                        />
                    </GridItem>
                )}
                {!!onPrintClick && (
                    <>
                        <GridItem className="print-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsPadding,
                                classes.tableSettingsFlexCenter,
                                'print-option-container'
                            )}
                        >
                            <Button onClick={onPrintClick}>
                                Print &nbsp;{' '}
                                <PrintIcon
                                    fontSize="small"
                                    className={classes.iconBorder}
                                />
                            </Button>
                        </GridItem>
                    </>
                )}
                {hasGrouping && !!onCollapseAllClick && !!onExpandAllClick && (
                    <>
                        <GridItem className="expand-collapse-all-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsPadding,
                                classes.tableSettingsFlexCenter,
                                classes.expandCollapseAllContainer,
                                'expand-collapse-all-container'
                            )}
                        >
                            <Button
                                variant="outlined"
                                endIcon={<KeyboardArrowDownIcon />}
                                className={clsx(
                                    classes.iconBorder,
                                    'expand-all-button'
                                )}
                                onClick={onExpandAllClick}
                            >
                                Expand All
                            </Button>
                            <Button
                                variant="outlined"
                                endIcon={<KeyboardArrowUpIcon />}
                                className={clsx(
                                    classes.iconBorder,
                                    'collapse-all-button'
                                )}
                                onClick={onCollapseAllClick}
                            >
                                Collapse All
                            </Button>
                        </GridItem>
                    </>
                )}
                {(!!onCardViewClick || !!onTableViewClick) && (
                    <>
                        <GridItem className="view-as-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.viewAsContainer,
                                classes.tableSettingsPadding,
                                'view-as-option-container'
                            )}
                        >
                            <span>View as:</span>
                            <Button
                                onClick={onCardViewClick}
                                className="view-as-option-card-button"
                                data-testid="btnViewAsCard"
                            >
                                <ViewModuleIcon
                                    fontSize="small"
                                    className={clsx(
                                        !tableViewActive &&
                                            classes.activeIconBackgroundColor,
                                        'view-as-card',
                                        !tableViewActive && 'active'
                                    )}
                                />
                                &nbsp; Card
                            </Button>
                            <Button
                                onClick={onTableViewClick}
                                className="view-as-option-table-button"
                                data-testid="btnViewAsTable"
                            >
                                <ListIcon
                                    fontSize="small"
                                    className={clsx(
                                        tableViewActive &&
                                            classes.activeIconBackgroundColor,
                                        'view-as-table',
                                        tableViewActive && 'active'
                                    )}
                                />
                                &nbsp; Table
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!showClearAllFilterButton && (
                    <>
                        <GridItem className="clear-filter-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="clear-filter-container">
                            <Button
                                onClick={onClearAllFilterClick}
                                className="clear-filter-button"
                            >
                                Clear Filter
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!onFilterClick && (
                    <>
                        <GridItem className="filter-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="filter-option-container">
                            <IconButton
                                onClick={handleFilterClick}
                                className="filter-option-button"
                                data-testid="btnDisplayProjectFilters"
                            >
                                <FilterListIcon
                                    fontSize="small"
                                    className={clsx(
                                        showFiltersRow &&
                                            classes.activeIconBackgroundColor,
                                        'view-filter',
                                        showFiltersRow && 'active'
                                    )}
                                />
                            </IconButton>
                        </GridItem>
                    </>
                )}
                {showTableSettings && !!tableSettings && (
                    <>
                        <GridItem className="table-settings-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="table-settings-option-container">
                            <TableSettings {...tableSettings} />
                        </GridItem>
                    </>
                )}
            </GridItem>
        </GridContainer>
    )
}

export default SpecTableTitleSettings
